import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  ShoppingCart, Plus, Trash, Check, CircleNotch, ArrowLeft
} from '@phosphor-icons/react';
import { toast } from 'sonner';

import { API } from '../config/api';

export default function PurchaseBuilderPage() {
  const { id: docId } = useParams();
  const isEditMode = !!docId;
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplierId, setSelectedSupplierId] = useState('');
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    due_date: '',
    supplier_name: '',
    supplier_vat: '',
    supplier_invoice_number: '',
    supplier_invoice_date: '',
    iva_rate: 22,
    payment_status: 'da_pagare',
    payment_date: '',
    category: 'materiali',
    notes: ''
  });

  const [items, setItems] = useState([
    { description: '', unit: 'pz', quantity: 1, price: '' }
  ]);

  const fetchSuppliers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/suppliers`);
      setSuppliers(Array.isArray(data) ? data : [])
    } catch (err) { console.error(err); }
  }, []);

  const fetchDoc = useCallback(async () => {
    if (!docId) return;
    try {
      const { data } = await axios.get(`${API}/purchases/${docId}`);
      setForm({
        date: data.date || '',
        due_date: data.due_date || '',
        supplier_name: data.supplier_name || '',
        supplier_vat: data.supplier_vat || '',
        supplier_invoice_number: data.supplier_invoice_number || '',
        supplier_invoice_date: data.supplier_invoice_date || '',
        iva_rate: data.iva_rate ?? 22,
        payment_status: data.payment_status || 'da_pagare',
        payment_date: data.payment_date || '',
        category: data.category || 'materiali',
        notes: data.notes || ''
      });
      setItems(data.items?.length ? data.items : [{ description: '', unit: 'pz', quantity: 1, price: '' }]);
      setSelectedSupplierId(data.supplier_id || '');
    } catch (err) {
      toast.error(t('purchase.loadError'));
      navigate('/acquisti');
    }
  }, [docId, navigate, t]);

  useEffect(() => { fetchSuppliers(); fetchDoc(); }, [fetchSuppliers, fetchDoc]);

  const handleSupplierSelect = (supplierId) => {
    setSelectedSupplierId(supplierId);
    const s = (suppliers || []).find(x => x.id === supplierId);
    if (s) {
      setForm(f => ({
        ...f,
        supplier_name: s.name || '',
        supplier_vat: s.vat_number || s.email || '',
      }));
    }
  };

  const addItem = () => {
    setItems([...items, { description: '', unit: 'pz', quantity: 1, price: '' }]);
  };

  const removeItem = (index) => {
    if ((items || []).length <= 1) return;
    setItems((items || []).filter((_, i) => i !== index));
  };

  const updateItem = (index, field, value) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };
    setItems(updated);
  };

  const subtotal = (items || []).reduce((sum, item) => sum + (parseFloat(item.price) || 0) * (parseFloat(item.quantity) || 0), 0);
  const ivaAmount = subtotal * (parseFloat(form.iva_rate) || 0) / 100;
  const total = subtotal + ivaAmount;

  const handleSave = async () => {
    if (!form.supplier_name) {
      toast.error(t('purchase.selectSupplier'));
      return;
    }
    if ((items || []).length === 0 || !items[0].description) {
      toast.error(t('purchase.addItem'));
      return;
    }

    setSaving(true);
    const payload = {
      ...form,
      supplier_id: selectedSupplierId,
      items: (items || []).filter(i => i.description)
    };

    try {
      if (isEditMode) {
        await axios.put(`${API}/purchases/${docId}`, payload);
        toast.success(t('purchase.updated'));
      } else {
        await axios.post(`${API}/purchases`, payload);
        toast.success(t('purchase.created'));
      }
      navigate('/acquisti');
    } catch (err) {
      toast.error(isEditMode ? t('purchase.updateError') : t('purchase.createError'));
    }
    setSaving(false);
  };

  const formatCurrency = (val) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(val);

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/acquisti')} data-testid="back-to-purchases">
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="font-outfit text-2xl font-bold text-[#0A0F1A] flex items-center gap-2">
            <ShoppingCart size={24} weight="duotone" className="text-[#0033A0]" />
            {isEditMode ? t('purchase.editPurchase') : t('purchase.newPurchase')}
          </h1>
        </div>
      </div>

      {/* Supplier & Invoice Reference */}
      <Card className="border-[#E2E8F0]">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-bold text-[#0033A0] uppercase tracking-wider">
            {t('purchase.section1')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs">{t('purchase.selectSupplier')}</Label>
              <Select value={selectedSupplierId} onValueChange={handleSupplierSelect}>
                <SelectTrigger className="mt-1" data-testid="purchase-supplier-select">
                  <SelectValue placeholder={t('purchase.selectSupplierPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {(suppliers || []).map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t('purchase.supplier')}</Label>
              <Input value={form.supplier_name} onChange={e => setForm({ ...form, supplier_name: e.target.value })} className="mt-1" data-testid="purchase-supplier-name" />
            </div>
            <div>
              <Label className="text-xs">P.IVA Fornitore</Label>
              <Input value={form.supplier_vat} onChange={e => setForm({ ...form, supplier_vat: e.target.value })} className="mt-1" data-testid="purchase-supplier-vat" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs">{t('purchase.supplierInvoiceNumber')}</Label>
              <Input value={form.supplier_invoice_number} onChange={e => setForm({ ...form, supplier_invoice_number: e.target.value })} className="mt-1" placeholder="N. fattura fornitore" data-testid="purchase-supplier-inv-num" />
            </div>
            <div>
              <Label className="text-xs">{t('purchase.supplierInvoiceDate')}</Label>
              <Input type="date" value={form.supplier_invoice_date} onChange={e => setForm({ ...form, supplier_invoice_date: e.target.value })} className="mt-1" data-testid="purchase-supplier-inv-date" />
            </div>
            <div>
              <Label className="text-xs">{t('purchase.date')}</Label>
              <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="mt-1" data-testid="purchase-date" />
            </div>
            <div>
              <Label className="text-xs">{t('purchase.dueDate')}</Label>
              <Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} className="mt-1" data-testid="purchase-due-date" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">{t('purchase.category')}</Label>
              <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1" data-testid="purchase-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="materiali">{t('purchase.categories.materiali')}</SelectItem>
                  <SelectItem value="servizi">{t('purchase.categories.servizi')}</SelectItem>
                  <SelectItem value="attrezzature">{t('purchase.categories.attrezzature')}</SelectItem>
                  <SelectItem value="trasporti">{t('purchase.categories.trasporti')}</SelectItem>
                  <SelectItem value="altro">{t('purchase.categories.altro')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t('purchase.paymentStatus')}</Label>
              <Select value={form.payment_status} onValueChange={v => setForm({ ...form, payment_status: v })}>
                <SelectTrigger className="mt-1" data-testid="purchase-payment-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="da_pagare">{t('purchase.paymentLabels.da_pagare')}</SelectItem>
                  <SelectItem value="parziale">{t('purchase.paymentLabels.parziale')}</SelectItem>
                  <SelectItem value="pagato">{t('purchase.paymentLabels.pagato')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Items */}
      <Card className="border-[#E2E8F0]">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-bold text-[#0033A0] uppercase tracking-wider">
            {t('purchase.section2')}
          </CardTitle>
          <Button variant="outline" size="sm" onClick={addItem} data-testid="purchase-add-item">
            <Plus size={16} className="mr-1" />
            {t('purchase.addItem')}
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {(items || []).map((item, index) => (
            <div key={index} className="flex items-start gap-3 p-3 bg-[#F4F6F8] rounded-lg" data-testid={`purchase-item-${index}`}>
              <span className="text-xs font-bold text-[#0033A0] mt-2 w-6 text-center">{index + 1}</span>
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-12 gap-3">
                <div className="sm:col-span-5">
                  <Label className="text-[10px] text-[#4A5568]">{t('purchase.description')}</Label>
                  <Input
                    value={item.description}
                    onChange={e => updateItem(index, 'description', e.target.value)}
                    className="mt-0.5 bg-white text-sm"
                    data-testid={`purchase-item-desc-${index}`}
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-[10px] text-[#4A5568]">{t('purchase.unit')}</Label>
                  <Select value={item.unit} onValueChange={v => updateItem(index, 'unit', v)}>
                    <SelectTrigger className="mt-0.5 bg-white text-sm" data-testid={`purchase-item-unit-${index}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pz">pz</SelectItem>
                      <SelectItem value="ml">ml</SelectItem>
                      <SelectItem value="mq">mq</SelectItem>
                      <SelectItem value="kg">kg</SelectItem>
                      <SelectItem value="nr">nr</SelectItem>
                      <SelectItem value="ore">ore</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-1">
                  <Label className="text-[10px] text-[#4A5568]">{t('purchase.quantity')}</Label>
                  <Input
                    type="number" min="1"
                    value={item.quantity}
                    onChange={e => updateItem(index, 'quantity', parseFloat(e.target.value) || 1)}
                    className="mt-0.5 bg-white text-sm"
                    data-testid={`purchase-item-qty-${index}`}
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-[10px] text-[#4A5568]">{t('purchase.unitPrice')}</Label>
                  <Input
                    type="number" min="0" step="0.01"
                    value={item.price}
                    onChange={e => updateItem(index, 'price', parseFloat(e.target.value) || 0)}
                    className="mt-0.5 bg-white text-sm"
                    data-testid={`purchase-item-price-${index}`}
                  />
                </div>
                <div className="sm:col-span-1 text-right pt-5">
                  <span className="text-sm font-medium">
                    {formatCurrency((parseFloat(item.price) || 0) * (parseFloat(item.quantity) || 0))}
                  </span>
                </div>
                <div className="sm:col-span-1 flex items-end">
                  {(items || []).length > 1 && (
                    <Button variant="ghost" size="icon" onClick={() => removeItem(index)} className="text-red-500 hover:bg-red-50 h-9 w-9" data-testid={`purchase-item-remove-${index}`}>
                      <Trash size={16} />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}

          <div className="border-t border-[#E2E8F0] pt-4 mt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-[#4A5568]">{t('purchase.subtotal')}</span>
              <span className="font-medium" data-testid="purchase-subtotal">{formatCurrency(subtotal)}</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <div className="flex items-center gap-2">
                <span className="text-[#4A5568]">IVA</span>
                <Input
                  type="number" min="0" max="100" step="1"
                  value={form.iva_rate}
                  onChange={e => setForm({ ...form, iva_rate: parseFloat(e.target.value) || 0 })}
                  className="w-16 h-7 text-xs text-center"
                  data-testid="purchase-iva-rate"
                />
                <span className="text-[#4A5568]">%</span>
              </div>
              <span className="font-medium" data-testid="purchase-iva-amount">{formatCurrency(ivaAmount)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold border-t border-[#0033A0] pt-2">
              <span className="text-[#0033A0]">{t('purchase.total')}</span>
              <span className="text-[#0033A0]" data-testid="purchase-total">{formatCurrency(total)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notes */}
      <Card className="border-[#E2E8F0]">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-bold text-[#0033A0] uppercase tracking-wider">
            {t('purchase.section3')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={form.notes}
            onChange={e => setForm({ ...form, notes: e.target.value })}
            rows={3}
            placeholder={t('purchase.notesPlaceholder')}
            className="border-[#E2E8F0]"
            data-testid="purchase-notes"
          />
        </CardContent>
      </Card>

      {/* Save */}
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => navigate('/acquisti')} data-testid="purchase-cancel">
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#0033A0] hover:bg-[#0047AB] text-white min-w-[180px]"
          data-testid="purchase-save"
        >
          {saving ? (
            <><CircleNotch size={18} className="mr-2 animate-spin" /> {isEditMode ? t('purchase.saving') : t('purchase.creating')}</>
          ) : (
            <><Check size={18} className="mr-2" /> {isEditMode ? t('purchase.savePurchase') : t('purchase.createPurchase')}</>
          )}
        </Button>
      </div>
    </div>
  );
}

